// useSafeRequest.ts
import { useCallback, useRef, useState } from "react";
import type { AxiosInstance, AxiosRequestConfig } from "axios";
import { withRetry, type RetryOptions, defaultRetryOn } from "../functions/retry";
import { getErrorMessage, getHttpStatus } from "../functions/errors";

export interface RequestState<T> {
    data?: T;
    loading: boolean;
    error?: string;
    attempts: number;
}

export type RunOptions = {
    retry?: Partial<RetryOptions> | false; // false to disable retries for this call
    friendlyErrors?: boolean;              // default true
};

const DEFAULT_RETRY: RetryOptions = {
    maxAttempts: 5,
    baseDelayMs: 400,
    maxDelayMs: 12000,
    jitter: true,
    retryOn: defaultRetryOn,
};

export function useSafeRequest<T = unknown>(
    client: AxiosInstance,
    defaultRetry?: Partial<RetryOptions> | false
) {
    const [state, setState] = useState<RequestState<T>>({ loading: false, attempts: 0 });
    const abortRef = useRef<AbortController | null>(null);

    const run = useCallback(
        async (config: AxiosRequestConfig, opts: RunOptions = {}) => {
            const mergedRetry =
                opts.retry === false
                    ? undefined
                    : { ...DEFAULT_RETRY, ...(defaultRetry || {}), ...(opts.retry || {}) };

            setState((s) => ({ ...s, loading: true, error: undefined }));

            abortRef.current?.abort();
            const controller = new AbortController();
            abortRef.current = controller;

            const doRequest = async () => {
                const res = await client.request<T>({ ...config, signal: controller.signal });
                return res.data;
            };

            try {
                const data = mergedRetry
                    ? await withRetry<T>(
                        (_attempt) => doRequest(),
                        mergedRetry,
                        controller.signal,
                        (attempt, err) => {
                            if (err) {
                                setState((s) => ({
                                    ...s,
                                    attempts: attempt,
                                    error:
                                        opts.friendlyErrors === false
                                            ? getErrorMessage(err) 
                                            : s.error,   
                                }));
                            } else {
                                setState((s) => ({ ...s, attempts: attempt }));
                            }
                        }
                    )
                    : await doRequest();

                setState({ data, loading: false, attempts: Math.max(state.attempts, 1), error: undefined });
                return data;
            } catch (err: unknown) {
                const status = getHttpStatus(err);
                const msg =
                    opts.friendlyErrors === false
                        ? getErrorMessage(err)
                        : status
                            ? `Request failed (HTTP ${status}).`
                            : "We’re having trouble reaching the service.";

                setState((s) => ({ ...s, loading: false, error: msg, attempts: (s.attempts || 0) + 1 }));
                throw err; // keep throwing so callers can handle
            }
        },
        [client, defaultRetry, state.attempts]
    );

    const retry = useCallback(
        async (config: AxiosRequestConfig, opts?: RunOptions) => run(config, opts),
        [run]
    );

    const cancel = useCallback(() => {
        abortRef.current?.abort();
        abortRef.current = null;
        setState((s) => ({ ...s, loading: false }));
    }, []);

    return { ...state, run, retry, cancel };
}

